import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { ADVISORS_MY_FOUNDERS_PAGE_SIZE, ADVISORS_ORDER_BY_LAST_ACTIVITY } from '@src/components/sco/Advisors/constants';
import { MyFoundersLayout } from '@src/components/sco/Advisors/layout/MyFoundersLayout';
import { OpportunitiesAccessGate } from '@src/components/sco/Advisors/layout/OpportunitiesLayout';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { AdvisorsQueryState } from '@src/components/sco/Advisors/shared/AdvisorsQueryState';
import { EngagementSearchList, EngagementSearchListItem } from '@src/components/sco/Advisors/shared/EngagementSearchList';
import { founderDisplayName, founderSubtitle, isActiveFounderEngagement, myFoundersHref } from '@src/components/sco/Advisors/utils/mapFounderEngagements';
import { ADVISOR_ENGAGEMENT_STATUS_ACTIVE } from '@src/constants/advisor-engagement-statuses';
import { ADVISOR_FOUNDER_ENGAGEMENTS } from '@src/gql/queries/AdvisorFounderEngagements';
import { ScoAdvisorEngagement } from '@src/graphql-types';
import { useDebounce } from '@uidotdev/usehooks';
import { useEffect, useMemo, useState } from 'react';
type FounderEngagementsResult = {
  advisorEngagements?: {
    items?: Array<ScoAdvisorEngagement | null> | null;
    pagination?: {
      itemCount?: number | null;
      pageCount?: number | null;
      page?: number | null;
      pageSize?: number | null;
      hasNextPage?: boolean | null;
    } | null;
  } | null;
};

/** Supply side only: same gate as Opportunities (#25). */
export const MyFoundersListSection = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <OpportunitiesAccessGate><MyFoundersList /></OpportunitiesAccessGate>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const MyFoundersList = () => {
  const $ = _c(29);
  const paths = useAdvisorsPaths();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  let t0;
  if ($[0] !== search) {
    t0 = search.trim();
    $[0] = search;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const debouncedSearch = useDebounce(t0, 300);
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setPage(_temp);
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== debouncedSearch) {
    t2 = [debouncedSearch];
    $[3] = debouncedSearch;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [ADVISOR_ENGAGEMENT_STATUS_ACTIVE];
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = debouncedSearch || undefined;
  let t5;
  if ($[6] !== page || $[7] !== t4) {
    t5 = {
      statusKeys: t3,
      page,
      pageSize: ADVISORS_MY_FOUNDERS_PAGE_SIZE,
      search: t4,
      orderBy: ADVISORS_ORDER_BY_LAST_ACTIVITY
    };
    $[6] = page;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = debouncedSearch ? "network-only" : "cache-and-network";
  let t7;
  if ($[9] !== t5 || $[10] !== t6) {
    t7 = {
      variables: t5,
      fetchPolicy: t6,
      notifyOnNetworkStatusChange: true
    };
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const query = useQuery(ADVISOR_FOUNDER_ENGAGEMENTS, t7);
  const pagination = query.data?.advisorEngagements?.pagination;
  const itemCount = pagination?.itemCount ?? 0;
  const pageCount = Math.max(1, pagination?.pageCount ?? 1);
  query.data?.advisorEngagements?.items;
  let t8;
  if ($[12] !== paths.base || $[13] !== query.data?.advisorEngagements?.items) {
    const engagements = (query.data?.advisorEngagements?.items || []).filter(isActiveFounderEngagement) as ScoAdvisorEngagement[];
    let t9;
    if ($[15] !== paths.base) {
      t9 = engagement => {
        const founderUserId = engagement.founderUserId || engagement.founderUser?.userId;
        const name = founderDisplayName(engagement);
        const subtitle = founderSubtitle(engagement);
        const companyName = engagement.company?.name || engagement.founderUser?.company?.name;
        return {
          key: engagement.advisorEngagementId || engagement.id || founderUserId || name,
          name,
          href: myFoundersHref(founderUserId, paths.base),
          userId: founderUserId,
          subtitle,
          searchText: [name, subtitle, companyName, engagement.founderUser?.displayName].filter(Boolean).join(" ")
        };
      };
      $[15] = paths.base;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    t8 = engagements.map(t9);
    $[12] = paths.base;
    $[13] = query.data?.advisorEngagements?.items;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const items = t8;
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = value => {
      setSearch(value);
    };
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const handleSearchChange = t9;
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  if (!hasLoadedOnce && (query.data || query.error)) {
    setHasLoadedOnce(true);
  }
  const initialLoad = query.loading && !hasLoadedOnce && !query.data && !query.error;
  let t10;
  if ($[18] !== itemCount || $[19] !== items || $[20] !== page || $[21] !== pageCount || $[22] !== query.loading || $[23] !== search) {
    t10 = <EngagementSearchList emptyMessage="No active founder relationships yet." itemCount={itemCount} items={items} loading={query.loading} noMatchMessage="No founders match your search." page={page} pageCount={pageCount} search={search} searchPlaceholder="Search founders by name..." serverSide={true} onPageChange={setPage} onSearchChange={handleSearchChange} />;
    $[18] = itemCount;
    $[19] = items;
    $[20] = page;
    $[21] = pageCount;
    $[22] = query.loading;
    $[23] = search;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== initialLoad || $[26] !== query.error || $[27] !== t10) {
    t11 = <MyFoundersLayout activeKey="all"><AdvisorsQueryState error={query.error} loading={initialLoad}>{t10}</AdvisorsQueryState></MyFoundersLayout>;
    $[25] = initialLoad;
    $[26] = query.error;
    $[27] = t10;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  return t11;
};
function _temp(current) {
  return current === 1 ? current : 1;
}